import { css, Theme } from '@emotion/react';

/**
 * Оформление календаря кита. CSS react-datepicker не подключается вовсе (IMGM-1496): его
 * синий выбранный день, серая шапка и Helvetica не из темы, а перебивать каждое правило по
 * специфичности дороже, чем описать нужное с нуля. Отсюда правило: всё, что библиотека
 * рендерит и что видно, описано здесь — иначе всплывёт голым HTML.
 *
 * Стили подключаются классами (`ClassNames` из `@emotion/react`) в `calendarClassName`,
 * `popperClassName` и `wrapperClassName`: поповер — сосед поля, а не потомок, и
 * component selectors в проекте не работают (`ui.md`).
 */

/** Ячейка дня: 40 по горизонтали без зазора — промежуток диапазона рисуется сплошной лентой. */
const DAY_WIDTH = '40px';
const DAY_HEIGHT = '36px';

/**
 * Объявление для скринридера («Selected date: …»): глазу не видно, но из DOM не убираем.
 * Библиотека рендерит его в двух местах — в календаре и рядом с полем (в `input-container`,
 * после выбора времени), поэтому правило подключается и там, и там. Без него над полем
 * времени всплывала строка «Selected time: 12:15».
 */
const ariaLiveStyles = css`
  .react-datepicker__aria-live {
    position: absolute;
    width: 1px;
    height: 1px;
    margin: -1px;
    padding: 0;
    overflow: hidden;
    clip-path: circle(0);
    white-space: nowrap;
    border: 0;
  }
`;

/** Обёртка поля: то, что раньше давал CSS библиотеки, — иначе поле теряет ширину. */
export const wrapperStyles = () => css`
  display: inline-block;
  padding: 0;
  border: 0;

  ${ariaLiveStyles}

  .react-datepicker__input-container {
    position: relative;
    display: inline-block;
    width: 100%;
  }

  /*
   * Иконка календаря — часть поля, а не отдельная кнопка: клик проходит сквозь неё в поле и открывает
   * календарь так же, как клик по тексту. Без этого иконка лежала поверх поля и глотала клик.
   * Блок иконки — \`div\` с классом позиции (\`UDInput\`, \`IconContainer\`); у самого поля тот же класс, но это \`input\`.
   */
  .react-datepicker__input-container div.icon-right,
  .react-datepicker__input-container div.icon-left {
    pointer-events: none;
  }
`;

/** Поповер: слой выпадашки и отступ от поля; стрелки-треугольника нет, как у остальных поповеров кита. */
export const popperStyles = (theme: Theme) => css`
  z-index: ${theme.zIndex.dropdown};

  &[data-placement^='bottom'] {
    padding-top: 4px;
  }

  &[data-placement^='top'] {
    padding-bottom: 4px;
  }
`;

export const calendarStyles = (theme: Theme) => {
  const { colors } = theme;
  const day = '.react-datepicker__day';

  return css`
    position: relative;
    display: inline-flex;
    padding: 12px;
    background-color: ${colors.SurfaceCard};
    border: 1px solid ${colors.LineSubtle};
    border-radius: ${theme.radii.panel};
    box-shadow: ${theme.elevation.medium};
    color: ${colors.InkPrimary};
    font-family: var(--font-display);
    font-size: 14px;
    line-height: 1;
    user-select: none;

    ${ariaLiveStyles}

    .react-datepicker__month-container {
      position: relative;
      display: flex;
      flex-direction: column;
    }

    /* Заголовок — кнопка переключения вида (дни → месяцы → годы); стрелка поворачивается, когда открыт список. */
    .ud-calendar-switch {
      display: inline-flex;
      align-items: center;
      gap: 4px;
      height: 28px;
      padding: 0 8px;
      border-radius: ${theme.radii.card};
      color: ${colors.InkPrimary};
      cursor: pointer;
      transition: background-color 0.15s ease-out;

      &:hover {
        background-color: ${colors.SurfaceSunken};
      }

      &:focus-visible {
        outline: 2px solid ${colors.AccentJonquil};
        outline-offset: -2px;
      }

      svg {
        color: ${colors.InkMuted};
        transition: transform 0.2s ease-out;
      }

      &.is-open svg {
        transform: rotate(180deg);
      }
    }

    /*
     * Список месяцев или годов накрывает сетку дней: та не меняет размер календаря, а на это время
     * скрыта (visibility, не display) — без прыжка высоты, и из фокуса со скринридером тоже.
     */
    .react-datepicker__month-container:has(.ud-calendar-panel) .react-datepicker__day-names,
    .react-datepicker__month-container:has(.ud-calendar-panel) .react-datepicker__month {
      visibility: hidden;
    }

    .ud-calendar-panel {
      position: absolute;
      top: 32px;
      right: 0;
      bottom: 0;
      left: 0;
      z-index: 1;
      display: grid;
      grid-template-columns: repeat(3, minmax(0, 1fr));
      grid-auto-rows: 40px;
      align-content: center;
      gap: 4px;
      background-color: ${colors.SurfaceCard};
      animation: ud-calendar-panel-in 0.15s ease-out both;
    }

    @keyframes ud-calendar-panel-in {
      from {
        opacity: 0;
      }
      to {
        opacity: 1;
      }
    }

    .ud-calendar-cell {
      position: relative;
      border-radius: ${theme.radii.card};
      color: ${colors.InkPrimary};
      cursor: pointer;
      transition:
        background-color 0.15s ease-out,
        color 0.15s ease-out;

      &:hover {
        background-color: ${colors.SurfaceSunken};
      }

      &:focus-visible {
        outline: 2px solid ${colors.AccentJonquil};
        outline-offset: -2px;
      }

      /* Текущий месяц или год — та же золотая рамка, что у «сегодня» в днях. */
      &.is-today::after {
        content: '';
        position: absolute;
        inset: 2px;
        border: 1.5px solid ${colors.DarkYellow700};
        border-radius: ${theme.radii.card};
        pointer-events: none;
      }

      &.is-current {
        background-color: ${colors.DarkYellow};
        font-weight: 600;

        &::after {
          display: none;
        }

        &:hover {
          background-color: ${colors.DarkYellow400};
        }
      }

      &:disabled {
        color: ${colors.InkDisabled};
        background-color: transparent;
        cursor: default;
      }
    }

    .react-datepicker__header {
      padding: 0;
      text-align: center;
    }

    .react-datepicker__day-names,
    .react-datepicker__week {
      display: flex;
      white-space: nowrap;
    }

    .react-datepicker__day-names {
      margin-top: 8px;
    }

    .react-datepicker__day-name {
      width: ${DAY_WIDTH};
      height: 28px;
      line-height: 28px;
      text-align: center;
      font-size: 12px;
      color: ${colors.InkMuted};
      text-transform: lowercase;
    }

    .react-datepicker__month {
      margin: 0;
    }

    ${day} {
      position: relative;
      width: ${DAY_WIDTH};
      height: ${DAY_HEIGHT};
      margin: 2px 0;
      line-height: ${DAY_HEIGHT};
      text-align: center;
      border-radius: ${theme.radii.card};
      cursor: pointer;
      outline: none;
      /*
       * Перекрашивание дня — плавное: при наведении будущий промежуток «наливается», а не вспыхивает;
       * радиус тоже анимируется — конец ленты скругляется, когда курсор уходит дальше.
       */
      transition:
        background-color 0.15s ease-out,
        color 0.15s ease-out,
        border-radius 0.15s ease-out;

      &::after {
        transition: opacity 0.15s ease-out;
      }

      &:hover {
        background-color: ${colors.SurfaceSunken};
      }

      /* Кольцо фокуса — отдельным слоем со своим скруглением: внутри полосы промежутка у ячейки радиуса нет. */
      &:focus-visible::before {
        content: '';
        position: absolute;
        inset: 2px;
        border: 2px solid ${colors.AccentJonquil};
        border-radius: ${theme.radii.card};
        pointer-events: none;
      }
    }

    /*
     * Сегодня — скруглённая золотая рамка и полужирная цифра. Рамка — отдельный слой со своим радиусом,
     * поэтому и в полосе промежутка (где у ячейки радиуса нет) она остаётся скруглённой, а не квадратной.
     * DarkYellow700: 4.3:1 на карточке и 3.7:1 на полосе — порог WCAG 1.4.11 для нетекстового элемента (3:1).
     * Скринридеру «сегодня» сообщает библиотека: aria-current="date".
     *
     * Курсор клавиатуры (--keyboard-selected) фоном не красится: библиотека вешает его на сегодняшний день
     * при каждом открытии, и он давал серый квадрат без всякой клавиатуры. При навигации стрелками фокус
     * переходит на сам день — его видно по кольцу :focus-visible.
     */
    ${day}--today {
      font-weight: 600;

      &::after {
        content: '';
        position: absolute;
        inset: 2px;
        border: 1.5px solid ${colors.DarkYellow700};
        border-radius: ${theme.radii.card};
        pointer-events: none;
      }
    }

    /* На жёлтом выбранном дне рамка лишняя: выделение и так сильнее отметки «сегодня». */
    ${day}--today${day}--selected::after,
    ${day}--today${day}--range-start::after,
    ${day}--today${day}--range-end::after,
    ${day}--today${day}--selecting-range-start::after,
    ${day}--today${day}--selecting-range-end::after {
      display: none;
    }

    /*
     * Дни соседнего месяца не показываем (как Material 3): серым они неотличимы от недоступных,
     * а будущие дни на аналитике недоступны как раз серым. Место в сетке остаётся — неделя не съезжает.
     */
    ${day}--outside-month {
      visibility: hidden;
      pointer-events: none;
    }

    /* Промежуток диапазона — сплошная лента: без скругления в середине, скруглены только концы и края недели. */
    ${day}--in-range,
    ${day}--in-selecting-range {
      background-color: ${colors.DarkYellow100};
      border-radius: 0;

      &:hover {
        background-color: ${colors.DarkYellow200};
      }
    }

    /* Пока выбирается новый конец, старый промежуток гаснет — иначе на экране два диапазона сразу. */
    .react-datepicker__month--selecting-range ${day}--in-range:not(${day}--in-selecting-range) {
      background-color: transparent;
    }

    ${day}--in-range:first-of-type,
    ${day}--in-selecting-range:first-of-type,
    ${day}--range-start,
    ${day}--selecting-range-start {
      border-top-left-radius: ${theme.radii.card};
      border-bottom-left-radius: ${theme.radii.card};
    }

    ${day}--in-range:last-of-type,
    ${day}--in-selecting-range:last-of-type,
    ${day}--range-end,
    ${day}--selecting-range-end {
      border-top-right-radius: ${theme.radii.card};
      border-bottom-right-radius: ${theme.radii.card};
    }

    /* Выбранный день и концы диапазона — V1: жёлтый с основным текстом (8.5:1). */
    ${day}--selected,
    ${day}--range-start,
    ${day}--range-end,
    ${day}--selecting-range-start,
    ${day}--selecting-range-end,
    .react-datepicker__month--selecting-range ${day}--range-start:not(${day}--in-selecting-range) {
      background-color: ${colors.DarkYellow};
      color: ${colors.InkPrimary};
      font-weight: 600;

      &:hover {
        background-color: ${colors.DarkYellow400};
      }
    }

    /* Отдельный день (не диапазон) скруглён целиком. */
    ${day}--selected:not(${day}--in-range):not(${day}--in-selecting-range) {
      border-radius: ${theme.radii.card};
    }

    ${day}--disabled, ${day}--excluded {
      color: ${colors.InkDisabled};
      background-color: transparent;
      cursor: default;

      &:hover {
        background-color: transparent;
      }
    }

    /* Список времени (showTimeSelectOnly): та же карточка, внутри — прокручиваемая колонка. */
    .react-datepicker__time-container {
      width: 96px;
    }

    .react-datepicker__header--time {
      padding-bottom: 8px;
    }

    .react-datepicker-time__header {
      font-size: 12px;
      color: ${colors.InkMuted};
    }

    .react-datepicker__time-box {
      width: 100%;
    }

    .react-datepicker__time-list {
      height: 216px;
      margin: 0;
      padding: 0;
      overflow-y: auto;
      list-style: none;
    }

    .react-datepicker__time-list-item {
      height: ${DAY_HEIGHT};
      line-height: ${DAY_HEIGHT};
      text-align: center;
      border-radius: ${theme.radii.card};
      cursor: pointer;
      transition:
        background-color 0.15s ease-out,
        color 0.15s ease-out;

      &:hover {
        background-color: ${colors.SurfaceSunken};
      }
    }

    .react-datepicker__time-list-item--selected {
      background-color: ${colors.DarkYellow};
      color: ${colors.InkPrimary};
      font-weight: 600;

      &:hover {
        background-color: ${colors.DarkYellow400};
      }
    }

    .react-datepicker__time-list-item--disabled {
      color: ${colors.InkDisabled};
      cursor: default;

      &:hover {
        background-color: transparent;
      }
    }
  `;
};
